/**
 * UploadManager Context
 * 
 * Manages multiple concurrent uploads globally across the application.
 * Uploads continue even when user navigates or switches tabs.
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { ChunkedUploader, shouldUseChunkedUpload } from '../utils/chunkedUploader';
import api from '../services/api';

const UploadManagerContext = createContext();

export const useUploadManager = () => {
    const context = useContext(UploadManagerContext);
    if (!context) {
        throw new Error('useUploadManager must be used within UploadManagerProvider');
    }
    return context;
};

export const UploadManagerProvider = ({ children }) => {
    const [uploads, setUploads] = useState({});
    const uploadsRef = useRef({});
    const nextIdRef = useRef(1);
    
    /**
     * Add a new upload
     */
    const addUpload = useCallback((file, options = {}) => {
        const uploadId = `upload-${Date.now()}-${nextIdRef.current++}`;
        
        const uploadInfo = {
            id: uploadId,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            folderId: options.folderId || null,
            description: options.description || null,
            status: 'pending', // pending, uploading, completing, completed, failed, aborted
            progress: 0,
            uploadedBytes: 0,
            totalBytes: file.size,
            error: null,
            startedAt: Date.now(),
            completedAt: null,
            useChunked: shouldUseChunkedUpload(file.size)
        };
        
        setUploads(prev => ({
            ...prev,
            [uploadId]: uploadInfo
        }));
        
        uploadsRef.current[uploadId] = uploadInfo;
        
        // Start upload
        if (uploadInfo.useChunked) {
            startChunkedUpload(uploadId, file, options);
        } else {
            startSimpleUpload(uploadId, file, options);
        }
        
        return uploadId;
    }, []);
    
    /**
     * Start chunked upload for large files
     */
    const startChunkedUpload = useCallback((uploadId, file, options) => {
        const uploader = new ChunkedUploader(file, {
            folderId: options.folderId,
            description: options.description,
            
            onProgress: (progressInfo) => {
                updateUpload(uploadId, {
                    status: 'uploading',
                    progress: progressInfo.percentage,
                    uploadedBytes: progressInfo.uploadedBytes,
                    totalBytes: progressInfo.totalBytes
                });
            },
            
            onStateChange: (state) => {
                // Update UI immediately when state changes
                if (state === 'initiating') {
                    updateUpload(uploadId, {
                        status: 'pending',
                        progress: 0
                    });
                } else if (state === 'uploading') {
                    updateUpload(uploadId, {
                        status: 'uploading',
                        progress: 0
                    });
                } else if (state === 'completing') {
                    updateUpload(uploadId, {
                        status: 'completing',
                        progress: 100
                    });
                }
            },
            
            onComplete: (document) => {
                updateUpload(uploadId, {
                    status: 'completed',
                    progress: 100,
                    completedAt: Date.now(),
                    document
                });
                
                // Call success callback if provided
                if (options.onSuccess) {
                    options.onSuccess(document);
                }
                
                // Auto-remove after delay
                setTimeout(() => {
                    removeUpload(uploadId);
                }, 5000);
            },
            
            onError: (error) => {
                updateUpload(uploadId, {
                    status: 'failed',
                    error: error.message || 'Upload failed'
                });
                
                // Call error callback if provided
                if (options.onError) {
                    options.onError(error);
                }
            }
        });
        
        // Store uploader instance for cancellation
        uploadsRef.current[uploadId].uploader = uploader;
        
        // Start upload
        uploader.start();
    }, []);
    
    /**
     * Start simple upload for small files
     */
    const startSimpleUpload = useCallback(async (uploadId, file, options) => {
        try {
            updateUpload(uploadId, {
                status: 'uploading',
                progress: 0
            });
            
            const formData = new FormData();
            formData.append('file', file);
            
            // Build query parameters
            const params = new URLSearchParams();
            if (options.folderId) {
                params.append('folder_id', options.folderId);
            }
            if (options.description) {
                params.append('description', options.description);
            }
            
            const uploadUrl = options.uploadUrl || `/library/documents/upload?${params.toString()}`;
            const response = await api.post(uploadUrl, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: (progressEvent) => {
                    const percentage = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    
                    updateUpload(uploadId, {
                        status: 'uploading',
                        progress: percentage,
                        uploadedBytes: progressEvent.loaded,
                        totalBytes: progressEvent.total
                    });
                }
            });
            
            updateUpload(uploadId, {
                status: 'completed',
                progress: 100,
                completedAt: Date.now(),
                document: response.data
            });
            
            // Call success callback if provided
            if (options.onSuccess) {
                options.onSuccess(response.data);
            }
            
            // Auto-remove after delay
            setTimeout(() => {
                removeUpload(uploadId);
            }, 5000);
            
        } catch (error) {
            updateUpload(uploadId, {
                status: 'failed',
                error: error.response?.data?.detail || error.message || 'Upload failed'
            });
            
            // Call error callback if provided
            if (options.onError) {
                options.onError(error);
            }
        }
    }, []);
    
    /**
     * Update an upload's state
     */
    const updateUpload = useCallback((uploadId, updates) => {
        setUploads(prev => ({
            ...prev,
            [uploadId]: {
                ...prev[uploadId],
                ...updates
            }
        }));
        
        if (uploadsRef.current[uploadId]) {
            uploadsRef.current[uploadId] = {
                ...uploadsRef.current[uploadId],
                ...updates
            };
        }
    }, []);
    
    /**
     * Cancel an upload
     */
    const cancelUpload = useCallback(async (uploadId) => {
        const upload = uploadsRef.current[uploadId];
        if (!upload) return;
        
        // Cancel chunked upload
        if (upload.uploader) {
            await upload.uploader.abort();
        }
        
        updateUpload(uploadId, {
            status: 'aborted',
            completedAt: Date.now()
        });
        
        // Remove after short delay
        setTimeout(() => {
            removeUpload(uploadId);
        }, 2000);
    }, []);
    
    /**
     * Remove an upload from the list
     */
    const removeUpload = useCallback((uploadId) => {
        setUploads(prev => {
            const newUploads = { ...prev };
            delete newUploads[uploadId];
            return newUploads;
        });
        
        delete uploadsRef.current[uploadId];
    }, []);
    
    /**
     * Clear completed uploads
     */
    const clearCompleted = useCallback(() => {
        setUploads(prev => {
            const newUploads = {};
            Object.keys(prev).forEach(id => {
                if (prev[id].status !== 'completed') {
                    newUploads[id] = prev[id];
                } else {
                    delete uploadsRef.current[id];
                }
            });
            return newUploads;
        });
    }, []);
    
    /**
     * Get active upload count
     */
    const getActiveCount = useCallback(() => {
        return Object.values(uploads).filter(
            u => u.status === 'uploading' || u.status === 'completing'
        ).length;
    }, [uploads]);

    const downloadFile = useCallback(async (doc) => {
        try {
            let url = doc.direct_url;
            if (!url) {
                const response = await api.get(`/library/documents/${doc.id}/url`);
                url = response.data.download_url;
            }
            if (!url) {
                console.error('No download URL available');
                return;
            }
            const a = document.createElement('a');
            a.href = url;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch (err) {
            console.error('Download failed:', err);
        }
    }, []);

    const value = {
        uploads,
        addUpload,
        cancelUpload,
        removeUpload,
        clearCompleted,
        getActiveCount,
        downloadFile,
    };
    
    return (
        <UploadManagerContext.Provider value={value}>
            {children}
        </UploadManagerContext.Provider>
    );
};
